import type { SessionsListResult } from "../types.ts";
import type { AgentRosterItem, ChatSubagentCard } from "../ui-types.ts";
import { cardsFromSpawnedSessions } from "../controllers/subagents.ts";

/** Must match gateway `FLEET_GRAPH_INBOUND_NODE_ID`. */
export const FLEET_GRAPH_INBOUND_NODE_ID = "inbound";

export type FleetGraphNodeKind = "agent" | "inbound";

export type FleetGraphEdgeKind = "spawn" | "route" | "live" | "escalation";

export type FleetGraphNode = {
  id: string;
  label: string;
  kind: FleetGraphNodeKind;
};

export type FleetGraphEdge = {
  from: string;
  to: string;
  kind: FleetGraphEdgeKind;
  label?: string;
  bindingId?: string;
  active?: boolean;
  /** Live subagent session to open when the edge is clicked. */
  childSessionKey?: string;
};

export type AgentFleetGraphSnapshot = {
  nodes: FleetGraphNode[];
  edges: FleetGraphEdge[];
  sessionAgentId?: string;
};

export type FleetGraphLayoutNode = FleetGraphNode & {
  x: number;
  y: number;
  width: number;
  height: number;
  column: number;
  workStatus?: AgentRosterItem["workStatus"];
  activeRuns?: number;
  sessionFocus?: boolean;
};

export type FleetGraphLayoutEdge = FleetGraphEdge & {
  path: string;
};

export type FleetGraphLayout = {
  width: number;
  height: number;
  nodes: FleetGraphLayoutNode[];
  edges: FleetGraphLayoutEdge[];
};

export const FLEET_GRAPH_NODE_WIDTH = 168;
export const FLEET_GRAPH_NODE_HEIGHT = 76;
const COLUMN_GAP = 96;
const ROW_GAP = 20;
const PADDING = 24;

function parseSessionAgentId(sessionKey: string): string | null {
  const key = sessionKey.trim();
  if (!key) {
    return null;
  }
  const parts = key.split(":");
  if (parts.length >= 2 && parts[0] === "agent") {
    return parts[1] ?? null;
  }
  return null;
}

function edgeKey(edge: Pick<FleetGraphEdge, "from" | "to" | "kind" | "label">): string {
  return `${edge.from}|${edge.to}|${edge.kind}|${edge.label ?? ""}`;
}

export function mergeLiveSessionEdges(
  graph: AgentFleetGraphSnapshot,
  sessionKey: string,
  subagents: ChatSubagentCard[],
  sessions: SessionsListResult | null,
): FleetGraphEdge[] {
  const sessionAgentId = graph.sessionAgentId ?? parseSessionAgentId(sessionKey) ?? undefined;
  if (!sessionAgentId) {
    return [];
  }

  const live = new Map<string, FleetGraphEdge>();
  const mark = (toId: string, active: boolean, childSessionKey?: string) => {
    const key = edgeKey({ from: sessionAgentId, to: toId, kind: "live" });
    const existing = live.get(key);
    if (existing) {
      existing.active = existing.active || active;
      if (childSessionKey?.trim() && (active || !existing.childSessionKey)) {
        existing.childSessionKey = childSessionKey.trim();
      }
      return;
    }
    live.set(key, {
      from: sessionAgentId,
      to: toId,
      kind: "live",
      active,
      childSessionKey: childSessionKey?.trim() || undefined,
    });
  };

  for (const card of subagents) {
    if (!card.agentId || card.agentId === sessionAgentId) {
      continue;
    }
    const active =
      card.status === "running" || card.status === "spawning" || card.status === "queued";
    mark(card.agentId, active, card.childSessionKey);
  }

  for (const card of cardsFromSpawnedSessions(sessionKey, sessions)) {
    if (!card.agentId || card.agentId === sessionAgentId) {
      continue;
    }
    mark(
      card.agentId,
      card.status === "running" || card.status === "spawning",
      card.childSessionKey,
    );
  }

  return Array.from(live.values());
}

/** Config/static edges only — strips ephemeral session overlay edges. */
export function stripEphemeralFleetEdges(graph: AgentFleetGraphSnapshot): AgentFleetGraphSnapshot {
  return {
    ...graph,
    edges: graph.edges.filter((e) => e.kind !== "live" && e.kind !== "escalation"),
  };
}

export function mergeFleetGraphWithLive(
  graph: AgentFleetGraphSnapshot,
  sessionKey: string,
  subagents: ChatSubagentCard[],
  sessions: SessionsListResult | null,
): AgentFleetGraphSnapshot {
  const configEdges: FleetGraphEdge[] = graph.edges.map((e) => ({
    ...e,
    kind: e.kind as FleetGraphEdgeKind,
  }));
  const liveEdges = mergeLiveSessionEdges(graph, sessionKey, subagents, sessions);
  const seen = new Set(configEdges.map((e) => edgeKey(e)));
  const merged = [...configEdges];
  for (const edge of liveEdges) {
    const key = edgeKey(edge);
    if (!seen.has(key)) {
      merged.push(edge);
      seen.add(key);
    } else {
      const idx = merged.findIndex((e) => edgeKey(e) === key);
      if (idx >= 0) {
        merged[idx] = { ...merged[idx], active: edge.active };
      }
    }
  }
  return { ...graph, edges: merged };
}

function assignColumn(
  nodeId: string,
  spawnSources: Set<string>,
  routeTargets: Set<string>,
): number {
  if (nodeId === FLEET_GRAPH_INBOUND_NODE_ID) {
    return 0;
  }
  if (spawnSources.has(nodeId)) {
    return 1;
  }
  if (routeTargets.has(nodeId) && !spawnSources.has(nodeId)) {
    return 2;
  }
  return spawnSources.has(nodeId) ? 1 : 2;
}

function bezierPath(x1: number, y1: number, x2: number, y2: number): string {
  const dx = Math.max(40, Math.abs(x2 - x1) * 0.45);
  return `M ${x1} ${y1} C ${x1 + dx} ${y1}, ${x2 - dx} ${y2}, ${x2} ${y2}`;
}

export type FleetGraphNodePosition = { x: number; y: number };

export function applyManualNodePositions(
  layout: FleetGraphLayout,
  positions: Record<string, FleetGraphNodePosition>,
): FleetGraphLayout {
  if (!Object.keys(positions).length) {
    return layout;
  }
  const nodes = layout.nodes.map((node) => {
    const pos = positions[node.id];
    if (!pos) {
      return node;
    }
    return { ...node, x: pos.x, y: pos.y };
  });
  const nodeById = new Map(nodes.map((n) => [n.id, n]));
  const layoutEdges: FleetGraphLayoutEdge[] = layout.edges.map((edge) => {
    const fromNode = nodeById.get(edge.from);
    const toNode = nodeById.get(edge.to);
    if (!fromNode || !toNode) {
      return edge;
    }
    const x1 = fromNode.x + fromNode.width;
    const y1 = fromNode.y + fromNode.height / 2;
    const x2 = toNode.x;
    const y2 = toNode.y + toNode.height / 2;
    return { ...edge, path: bezierPath(x1, y1, x2, y2) };
  });
  let maxX = 0;
  let maxY = 0;
  for (const node of nodes) {
    maxX = Math.max(maxX, node.x + node.width);
    maxY = Math.max(maxY, node.y + node.height);
  }
  return {
    width: Math.max(layout.width, maxX + PADDING),
    height: Math.max(layout.height, maxY + PADDING),
    nodes,
    edges: layoutEdges,
  };
}

/**
 * The only roster fields the layout reads off a node.
 *
 * JAISIU-types-sweep: `layoutFleetGraph` was typed to take the full
 * `AgentRosterItem`, but it only ever reads `workStatus` and
 * `activeRuns` (see the node loop below). Callers that legitimately
 * hold a partial map — `execution-dashboard` passed a
 * `{ workStatus?, activeRuns? }` map and had to cast away the rest —
 * can now do so without a cast, because both fields are optional and a
 * node with no roster entry renders exactly like a node whose entry is
 * missing them (`roster?.workStatus` → `undefined`).
 */
export type FleetGraphRosterEntry = Pick<AgentRosterItem, "workStatus" | "activeRuns">;

export function layoutFleetGraph(
  graph: AgentFleetGraphSnapshot,
  rosterById: Map<string, FleetGraphRosterEntry>,
  containerWidth = 720,
  manualPositions?: Record<string, FleetGraphNodePosition>,
): FleetGraphLayout {
  const spawnSources = new Set(graph.edges.filter((e) => e.kind === "spawn").map((e) => e.from));
  const routeTargets = new Set(graph.edges.filter((e) => e.kind === "route").map((e) => e.to));

  const byColumn = new Map<number, FleetGraphNode[]>();
  for (const node of graph.nodes) {
    const col = assignColumn(node.id, spawnSources, routeTargets);
    const list = byColumn.get(col) ?? [];
    list.push(node);
    byColumn.set(col, list);
  }

  const columns = [...byColumn.keys()].sort((a, b) => a - b);
  const colCount = Math.max(columns.length, 1);
  const innerWidth = Math.max(
    containerWidth - PADDING * 2,
    colCount * FLEET_GRAPH_NODE_WIDTH + (colCount - 1) * COLUMN_GAP,
  );
  const colWidth =
    colCount > 1 ? (innerWidth - (colCount - 1) * COLUMN_GAP) / colCount : FLEET_GRAPH_NODE_WIDTH;

  const layoutNodes: FleetGraphLayoutNode[] = [];
  let maxColumnHeight = 0;

  for (const col of columns) {
    const nodes = byColumn.get(col) ?? [];
    nodes.sort((a, b) => a.label.localeCompare(b.label));
    const stackHeight =
      nodes.length * FLEET_GRAPH_NODE_HEIGHT + Math.max(0, nodes.length - 1) * ROW_GAP;
    maxColumnHeight = Math.max(maxColumnHeight, stackHeight);
    const colX = PADDING + col * (colWidth + COLUMN_GAP);
    let y = PADDING;
    for (const node of nodes) {
      const roster = rosterById.get(node.id);
      layoutNodes.push({
        ...node,
        x: colX + Math.max(0, (colWidth - FLEET_GRAPH_NODE_WIDTH) / 2),
        y,
        width: FLEET_GRAPH_NODE_WIDTH,
        height: FLEET_GRAPH_NODE_HEIGHT,
        column: col,
        workStatus: roster?.workStatus,
        activeRuns: roster?.activeRuns,
        sessionFocus: graph.sessionAgentId === node.id,
      });
      y += FLEET_GRAPH_NODE_HEIGHT + ROW_GAP;
    }
  }

  const height = Math.max(maxColumnHeight + PADDING * 2, FLEET_GRAPH_NODE_HEIGHT + PADDING * 2);
  const width = innerWidth + PADDING * 2;
  const nodeById = new Map(layoutNodes.map((n) => [n.id, n]));

  const layoutEdges: FleetGraphLayoutEdge[] = graph.edges
    .map((edge) => {
      const from = nodeById.get(edge.from);
      const to = nodeById.get(edge.to);
      if (!from || !to) {
        return null;
      }
      const x1 = from.x + from.width;
      const y1 = from.y + from.height / 2;
      const x2 = to.x;
      const y2 = to.y + to.height / 2;
      return {
        ...edge,
        path: bezierPath(x1, y1, x2, y2),
      };
    })
    .filter((e): e is FleetGraphLayoutEdge => e !== null);

  const base = { width, height, nodes: layoutNodes, edges: layoutEdges };
  if (manualPositions && Object.keys(manualPositions).length > 0) {
    return applyManualNodePositions(base, manualPositions);
  }
  return base;
}

export function isUnknownFleetGraphMethodError(err: unknown): boolean {
  return String(err).includes("unknown method: agents.fleet.graph");
}

export function formatFleetGraphError(err: unknown): string {
  if (isUnknownFleetGraphMethodError(err)) {
    return "Agent fleet map needs a newer gateway. Rebuild (pnpm build) and restart the gateway, then refresh.";
  }
  return String(err);
}

// ─────────────────────────────────────────────────────────────────────────────
// Phase 5.1 – Execution Dashboard types
// ─────────────────────────────────────────────────────────────────────────────

/** Phase of an agent within a live execution run. */
export type ExecutionAgentPhase =
  | "idle" // configured, not yet running
  | "queued" // waiting for dependency
  | "running" // actively working
  | "done" // completed successfully
  | "failed" // errored
  | "skipped"; // bypassed (parent failed, condition false, etc.)

/** A single agent in a live execution snapshot. */
export type ExecutionAgentState = {
  id: string;
  label: string;
  phase: ExecutionAgentPhase;
  /** Human-readable current step description. */
  step?: string;
  /** 0–1 progress fraction, if known. */
  progress?: number;
  /** Epoch-ms of last state change. */
  updatedAt?: number;
};

/** A directed edge in the execution graph (data/control flow). */
export type ExecutionFlowEdge = {
  from: string;
  to: string;
  /** Whether data is actively flowing right now. */
  active?: boolean;
};

/** Full execution snapshot produced by the execution engine. */
export type ExecutionSnapshot = {
  executionId?: string;
  agents: ExecutionAgentState[];
  edges: ExecutionFlowEdge[];
  startedAt?: number;
};
